import { useEffect } from "react";

/**
 * Hook that monitors for stuck body scroll states caused by modals.
 *
 * Radix UI dialogs/sheets set overflow:hidden on body when open,
 * but sometimes fail to restore it when closing (race conditions, unmounts, etc.)
 *
 * This hook uses a MutationObserver to detect when body has overflow:hidden
 * but no Radix dialog portals are open, and restores scroll automatically.
 *
 * Usage: Call once at App level - useBodyScrollFix();
 */
export function useBodyScrollFix() {
  useEffect(() => {
    // Check if body scroll is stuck (overflow:hidden but no dialogs open)
    const checkAndRestoreScroll = () => {
      const bodyStyle = document.body.style;
      const hasOverflowHidden = bodyStyle.overflow === "hidden";

      if (!hasOverflowHidden) return;

      // Check if any Radix dialog/sheet portal is actually open
      // Radix uses [data-radix-portal] for its portals and [data-state="open"] for open dialogs
      const openDialogs = document.querySelectorAll('[data-radix-portal] [data-state="open"]');

      if (openDialogs.length === 0) {
        // No dialogs open but body is locked - restore scroll
        bodyStyle.overflow = "";
        bodyStyle.paddingRight = "";
        bodyStyle.pointerEvents = "";
      }
    };

    // Observer for body style changes
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes" && mutation.attributeName === "style") {
          // Debounce slightly to let Radix finish its work
          setTimeout(checkAndRestoreScroll, 100);
          break;
        }
      }
    });

    // Also observe DOM changes (dialog portals being removed)
    const domObserver = new MutationObserver(() => {
      // When DOM changes, check if scroll needs restoration
      setTimeout(checkAndRestoreScroll, 100);
    });

    // Start observing
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    domObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Initial check
    checkAndRestoreScroll();

    return () => {
      observer.disconnect();
      domObserver.disconnect();
    };
  }, []);
}
